import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Checkbox, PinAction, SubmissionButtons } from '@components/common';

import { useManageSitesOptions } from '@hooks/useManageSitesOptions';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { resetRightSideControls, setPinnedFilter, setSitesFilters } from '@slices/map';

import { isEmpty } from '@utils/common';

import { TNameTitle } from '@interfaces/common/common';

import { EControlsType, TEControlsType } from '@enums/common';

import controlStyles from '../../../MapControls.module.scss';
import styles from '@components/Map/MapControls/Controls/Filters/Sites/Sites.module.scss';

interface IVendorCheckbox extends TNameTitle {
	isSelected: boolean;
}

const VendorsFilter = () => {
	const dispatch = useAppDispatch();
	const { appliedSitesFiltersValues } = useAppSelector(map);
	const { vendors } = useManageSitesOptions();

	const [vendorsItems, setVendorsItems] = useState<Array<IVendorCheckbox>>([]);
	const [isPinned, setIsPinned] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);

	const pinFilter = useCallback((filter: TEControlsType | null) => {
		return dispatch(setPinnedFilter(filter));
	}, [dispatch]);

	useEffect(() => {
		pinFilter(isPinned ? EControlsType.owners : null);
	}, [isPinned, pinFilter]);

	useEffect(() => {
		if (!isEmpty(vendors)) {
			setVendorsItems(vendors.map((vendor) => ({
				name: vendor.name,
				title: vendor.title,
				isSelected: appliedSitesFiltersValues.vendor.includes(vendor.name),
			})));
		}
	}, [appliedSitesFiltersValues.vendor, vendors]);

	useEffect(() => {
		if (!isEmpty(appliedSitesFiltersValues.vendor)) {
			setVendorsItems((prev) => {
				return prev.map((item) => {
					if (appliedSitesFiltersValues.vendor.includes(item.name)) {
						return {
							...item,
							isSelected: true,
						};
					}

					return {
						...item,
						isSelected: false,
					};
				});
			});
		}
	}, [appliedSitesFiltersValues.vendor]);

	const handleChangeVendor = (name: string) => () => {
		setVendorsItems((prev) => {
			return prev.map((item) => {
				if (item.name === name) {
					return {
						...item,
						isSelected: !item.isSelected,
					};
				}

				return item;
			});
		});
	};

	const resetVendors = () => {
		const vendors = vendorsItems.map((item) => {
			return {
				...item,
				isSelected: true,
			};
		});

		setVendorsItems(vendors);

		if (!isPinned) {
			const selectedVendors = vendors.filter((vendor) => vendor.isSelected);
			const isNoneSelected = selectedVendors.length === 0;

			if (isNoneSelected) {
				dispatch(setSitesFilters({
					vendor: ['none'],
				}));
			} else {
				dispatch(setSitesFilters({
					vendor: selectedVendors.map((vendor) => vendor.name),
				}));
			}

			dispatch(resetRightSideControls());
		}
	};

	const handleApply = () => {
		const selectedVendors = vendorsItems.filter((vendor) => vendor.isSelected);
		const isNoneSelected = selectedVendors.length === 0;

		if (isNoneSelected) {
			dispatch(setSitesFilters({
				vendor: ['none'],
			}));
		} else {
			dispatch(setSitesFilters({
				vendor: selectedVendors.map((vendor) => vendor.name),
			}));
		}

		if (!isPinned) dispatch(resetRightSideControls());
	};

	useOnClickOutside({
		refs: [containerRef],
		handler: () => dispatch(resetRightSideControls()),
		disableClickOutside: isPinned,
		fireEvent: 'click',
	});

	const isDirtyForm = useMemo(() => {
		if (appliedSitesFiltersValues.vendor.length === 0) {
			return !vendorsItems.every((item) => item.isSelected)
				&& !vendorsItems.every((item) => !item.isSelected);
		}

		return vendorsItems.some((item) => {
			if (item.isSelected) {
				return !appliedSitesFiltersValues.vendor.includes(item.name);
			}

			return appliedSitesFiltersValues.vendor.includes(item.name);
		});
	}, [
		appliedSitesFiltersValues.vendor,
		vendorsItems,
	]);

	return (
		<div
			ref={containerRef}
			className={controlStyles.controlItemContainer}
		>
			<div className={styles.header}>
				<div className={styles.sectionTitle}>Vendors</div>
				<PinAction
					isPinned={isPinned}
					pinTooltip='Pin'
					setIsPinned={setIsPinned}
					tooltipPlacement='left'
					unpinTooltip='Unpin'
				/>
			</div>
			{vendorsItems.map((vendor: IVendorCheckbox) => {
				return (
					<Checkbox
						key={vendor.name}
						checked={vendor.isSelected}
						label={vendor.title}
						name={vendor.name}
						onChange={handleChangeVendor(vendor.name)}
					/>
				);
			})}
			<SubmissionButtons
				disabledPositive={!isDirtyForm}
				negativeHandler={resetVendors}
				negativeLabel='Reset'
				positiveHandler={handleApply}
				positiveLabel='Apply'
			/>
		</div>
	);
};

export default memo(VendorsFilter);
